import { currencySchema, roleScopeLevelSchema } from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { moneySchema } from './common/money.js';
import { generatedFileSchema } from './generated-file.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

const isoDaySchema = z.iso.date('a day is a real YYYY-MM-DD date');

/** The four levels a total can be broken down by; `global` is the whole answer, not a row. */
export const REPORT_LEVELS = ['region', 'zone', 'country', 'institution'] as const;

export type ReportLevel = (typeof REPORT_LEVELS)[number];

/**
 * The widest window one report reads, in days counting both ends: a reporting
 * year, the same bound `MAX_CONSOLIDATION_DAYS` puts on a consolidation. A
 * comparison window is bounded separately and by the same number, so the
 * widest read is two years of buckets and never more.
 */
export const MAX_REPORT_DAYS = 366;

/** How many rows one report returns before it is a file rather than a screen. */
export const MAX_REPORT_ROWS = 500;

export const reportWindowSchema = z
  .object({ from: isoDaySchema, to: isoDaySchema })
  .refine((input) => input.from <= input.to, 'the period ends after it starts');

export type ReportWindow = z.infer<typeof reportWindowSchema>;

/**
 * One currency's measures over a window. Each keeps the definition
 * `CampaignTotalsRepository.measure` gives it, so a figure here is the figure
 * the campaign screen shows: `pledged` counts a donation that is not `draft`,
 * `rejected` or `cancelled`; `fulfillmentCost` is a `fulfilled` donation's cost
 * **in the cost's own currency**, which is routinely not the currency the
 * donation was pledged in; `collected` is the donation side of every confirmed
 * match.
 */
export const reportMeasuresSchema = z.object({
  currency: currencySchema,
  pledged: moneySchema,
  collected: moneySchema,
  fulfillmentCost: moneySchema,
  donationCount: z.number().int().min(0),
});

export type ReportMeasures = z.infer<typeof reportMeasuresSchema>;

/**
 * The same measures added up in one currency. **One rate day for the whole
 * window, named** — the last bulletin published on or before `to`, which is
 * `rateDate`. A consolidation converts each day at the bulletin before it,
 * because it reports one period's movements; a report exists to put two
 * periods beside each other, and two windows each converted at their own many
 * daily rates make the difference about the currency market rather than about
 * the money. So each window converts once, at its own last bulletin, and says
 * which — .claude/rules/money.md.
 *
 * **A currency with no bulletin is never guessed.** It is listed in
 * `unconvertedCurrencies`, kept out of `total`, and shown at its own figure —
 * §5.8's consolidation is the precedent.
 */
export const convertedTotalSchema = z.object({
  baseCurrency: currencySchema,
  pledged: moneySchema,
  collected: moneySchema,
  fulfillmentCost: moneySchema,
  donationCount: z.number().int().min(0),
  rateDate: isoDaySchema.nullable(),
  unconvertedCurrencies: z.array(currencySchema),
});

export type ConvertedTotal = z.infer<typeof convertedTotalSchema>;

export const reportNodeSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
});

export const donationTotalsRowSchema = z.object({
  node: reportNodeSchema,
  currencies: z.array(reportMeasuresSchema),
  converted: convertedTotalSchema,
  /** The same node over the comparison window; null when none was asked for. */
  previous: convertedTotalSchema.nullable(),
});

export type DonationTotalsRow = z.infer<typeof donationTotalsRowSchema>;

export const donationTotalsInputSchema = z
  .object({
    level: z.enum(REPORT_LEVELS).default('country'),
    from: isoDaySchema,
    to: isoDaySchema,
    compareFrom: isoDaySchema.optional(),
    compareTo: isoDaySchema.optional(),
    campaignId: z.uuid().optional(),
    campaignPeriodId: z.uuid().optional(),
  })
  .refine((input) => input.from <= input.to, 'the period ends after it starts')
  .refine(
    (input) =>
      (input.compareFrom === undefined) === (input.compareTo === undefined) &&
      (input.compareFrom === undefined ||
        (input.compareTo !== undefined && input.compareFrom <= input.compareTo)),
    'a comparison window needs both ends, and ends after it starts',
  );

export type DonationTotalsInput = z.infer<typeof donationTotalsInputSchema>;

/**
 * `asOf` is when the read model was last refreshed, so a screen can say how
 * current its figures are rather than implying they are live —
 * docs/plans/07-notifications-and-reporting.md §7.5.
 */
export const donationTotalsSchema = z.object({
  level: z.enum(REPORT_LEVELS),
  from: isoDaySchema,
  to: isoDaySchema,
  compareFrom: isoDaySchema.nullable(),
  compareTo: isoDaySchema.nullable(),
  asOf: z.string().nullable(),
  rows: z.array(donationTotalsRowSchema),
  /** Every row added together, converted once at the same rate day. */
  total: convertedTotalSchema,
  previousTotal: convertedTotalSchema.nullable(),
});

export type DonationTotals = z.infer<typeof donationTotalsSchema>;

export const campaignPerformanceRowSchema = z.object({
  campaignPeriodId: z.uuid(),
  campaignId: z.uuid(),
  campaignName: z.string(),
  campaignCode: z.string(),
  seasonYear: z.number().int().nullable(),
  /** What the period set out to raise; null when it has no goal. */
  goal: moneySchema.nullable(),
  currencies: z.array(reportMeasuresSchema),
  converted: convertedTotalSchema,
  /**
   * Collected against the goal in basis points, so a client never divides money.
   * Null when the period has no goal, or when the goal's currency could not be
   * reached from what was collected.
   */
  goalProgressBasisPoints: z.number().int().min(0).nullable(),
});

export type CampaignPerformanceRow = z.infer<typeof campaignPerformanceRowSchema>;

export const campaignPerformanceInputSchema = z
  .object({
    from: isoDaySchema,
    to: isoDaySchema,
    campaignId: z.uuid().optional(),
    seasonYear: z.coerce.number().int().min(2000).max(2200).optional(),
  })
  .refine((input) => input.from <= input.to, 'the period ends after it starts');

export type CampaignPerformanceInput = z.infer<typeof campaignPerformanceInputSchema>;

export const campaignPerformanceSchema = z.object({
  from: isoDaySchema,
  to: isoDaySchema,
  asOf: z.string().nullable(),
  rows: z.array(campaignPerformanceRowSchema),
  total: convertedTotalSchema,
});

export type CampaignPerformance = z.infer<typeof campaignPerformanceSchema>;

export const currencyAmountSchema = z.object({
  currency: currencySchema,
  amount: moneySchema,
  count: z.number().int().min(0),
});

/**
 * What has not come out even — PRD §10. **Both sides are read live**, not from
 * the rollup: an unmatched line and an unsettled donation are work waiting on
 * somebody today, and a figure a quarter of an hour old would send them to a
 * line that has since been matched.
 */
export const reconciliationDifferenceRowSchema = z.object({
  organization: reportNodeSchema,
  baseCurrency: currencySchema,
  unmatched: z.array(currencyAmountSchema),
  unsettled: z.array(currencyAmountSchema),
});

export type ReconciliationDifferenceRow = z.infer<typeof reconciliationDifferenceRowSchema>;

export const reconciliationDifferencesSchema = z.object({
  from: isoDaySchema,
  to: isoDaySchema,
  rows: z.array(reconciliationDifferenceRowSchema),
});

export type ReconciliationDifferences = z.infer<typeof reconciliationDifferencesSchema>;

export const DONOR_SEGMENTS = ['new', 'repeat', 'sponsor'] as const;

export type DonorSegment = (typeof DONOR_SEGMENTS)[number];

/**
 * **A donor falls in exactly one segment, and the order is `sponsor`, then
 * `repeat`, then `new`.** A sponsor is a donor with a live recurring pledge, so
 * they are almost always a repeat donor too; counting them twice would make the
 * segments add up to more than the donors. The precedence is stated here
 * because it is the whole meaning of the report.
 *
 * - `sponsor` — has a `pledges` row that is `active` or `paused` in the window.
 * - `repeat` — gave before the window opened, and again inside it.
 * - `new` — gave for the first time inside the window.
 */
export const donorSegmentRowSchema = z.object({
  segment: z.enum(DONOR_SEGMENTS),
  donorCount: z.number().int().min(0),
  donationCount: z.number().int().min(0),
  currencies: z.array(currencyAmountSchema),
  converted: convertedTotalSchema,
});

export type DonorSegmentRow = z.infer<typeof donorSegmentRowSchema>;

export const donorSegmentsSchema = z.object({
  from: isoDaySchema,
  to: isoDaySchema,
  rows: z.array(donorSegmentRowSchema),
});

export type DonorSegments = z.infer<typeof donorSegmentsSchema>;

export const fundTransferSummaryRowSchema = z.object({
  organization: reportNodeSchema,
  baseCurrency: currencySchema,
  outgoing: z.array(currencyAmountSchema),
  incoming: z.array(currencyAmountSchema),
  awaitingApproval: z.number().int().min(0),
  awaitingCompletion: z.number().int().min(0),
});

export type FundTransferSummaryRow = z.infer<typeof fundTransferSummaryRowSchema>;

export const fundTransferSummarySchema = z.object({
  from: isoDaySchema,
  to: isoDaySchema,
  rows: z.array(fundTransferSummaryRowSchema),
});

export type FundTransferSummary = z.infer<typeof fundTransferSummarySchema>;

export const seasonComparisonRowSchema = z.object({
  campaignPeriodId: z.uuid(),
  seasonYear: z.number().int().nullable(),
  startsOn: isoDaySchema,
  endsOn: isoDaySchema,
  goal: moneySchema.nullable(),
  currencies: z.array(reportMeasuresSchema),
  converted: convertedTotalSchema,
});

export type SeasonComparisonRow = z.infer<typeof seasonComparisonRowSchema>;

export const seasonComparisonInputSchema = z.object({ campaignId: z.uuid() });

export type SeasonComparisonInput = z.infer<typeof seasonComparisonInputSchema>;

/**
 * One campaign's seasons side by side — which is what the legacy system's
 * per-year clones were really for, and why a season is a `campaign_periods` row
 * here (docs/decisions/0011). Each season converts at **its own** last bulletin,
 * so a season reads in the money of its own year.
 */
export const seasonComparisonSchema = z.object({
  campaignId: z.uuid(),
  campaignName: z.string(),
  asOf: z.string().nullable(),
  rows: z.array(seasonComparisonRowSchema),
});

export type SeasonComparison = z.infer<typeof seasonComparisonSchema>;

export const DASHBOARD_PANELS = [
  'totals',
  'campaigns',
  'fulfilment',
  'reconciliation',
  'jobs',
] as const;

export type DashboardPanel = (typeof DASHBOARD_PANELS)[number];

export const dashboardPendingSchema = z.object({
  awaitingFulfilment: z.number().int().min(0).nullable(),
  unmatchedTransactions: z.number().int().min(0).nullable(),
  openJobFailures: z.number().int().min(0).nullable(),
});

export const dashboardDaySchema = z.object({
  day: isoDaySchema,
  collected: moneySchema,
  pledged: moneySchema,
});

export const dashboardCampaignSchema = z.object({
  campaignPeriodId: z.uuid(),
  campaignName: z.string(),
  seasonYear: z.number().int().nullable(),
  goal: moneySchema,
  collected: moneySchema,
  progressBasisPoints: z.number().int().min(0),
});

/**
 * The landing screen, decided by the server — docs/plans/07-notifications-and-reporting.md
 * §7.3. **Which panels a person gets is answered here, never worked out from
 * their roles in the browser**: `panels` names what this caller may see, and
 * every figure outside it is `null` rather than zero, so a screen can tell "you
 * have nothing waiting" from "this is not your work" — the same shape as
 * `manageable: false` on a user row (.claude/rules/rbac-scope.md).
 */
export const dashboardSchema = z.object({
  scopeLevel: roleScopeLevelSchema,
  /** The caller's own node; null at global scope, where the client names the level itself. */
  scopeName: z.string().nullable(),
  panels: z.array(z.enum(DASHBOARD_PANELS)),
  from: isoDaySchema,
  to: isoDaySchema,
  compareFrom: isoDaySchema,
  compareTo: isoDaySchema,
  asOf: z.string().nullable(),
  totals: convertedTotalSchema.nullable(),
  previousTotals: convertedTotalSchema.nullable(),
  daily: z.array(dashboardDaySchema),
  campaigns: z.array(dashboardCampaignSchema),
  pending: dashboardPendingSchema,
});

export type Dashboard = z.infer<typeof dashboardSchema>;

/**
 * PRD §10's reports, read from the `donation_rollups` read model rather than
 * from wide joins across the donation table — docs/plans/07-notifications-and-reporting.md
 * §7.4 and §7.5. Every read is scoped in SQL and every export is a
 * `generated_files` row the worker fills, with the requester's scope resolved
 * again at generation time.
 */
export const reportContract = {
  /** The landing screen. A session and no permission: what it holds is what the caller may reach. */
  dashboard: oc
    .route({ method: 'GET', path: '/reports/dashboard' })
    .errors(guarded)
    .input(z.object({}))
    .output(dashboardSchema),

  donationTotals: oc
    .route({ method: 'GET', path: '/reports/donation-totals' })
    .errors(guarded)
    .input(donationTotalsInputSchema)
    .output(donationTotalsSchema),

  campaignPerformance: oc
    .route({ method: 'GET', path: '/reports/campaign-performance' })
    .errors(guarded)
    .input(campaignPerformanceInputSchema)
    .output(campaignPerformanceSchema),

  reconciliationDifferences: oc
    .route({ method: 'GET', path: '/reports/reconciliation-differences' })
    .errors(guarded)
    .input(reportWindowSchema)
    .output(reconciliationDifferencesSchema),

  donorSegments: oc
    .route({ method: 'GET', path: '/reports/donor-segments' })
    .errors(guarded)
    .input(reportWindowSchema)
    .output(donorSegmentsSchema),

  fundTransferSummary: oc
    .route({ method: 'GET', path: '/reports/fund-transfers' })
    .errors(guarded)
    .input(reportWindowSchema)
    .output(fundTransferSummarySchema),

  seasonComparison: oc
    .route({ method: 'GET', path: '/reports/season-comparison' })
    .errors(guarded)
    .input(seasonComparisonInputSchema)
    .output(seasonComparisonSchema),

  /**
   * Each export is the same filter the screen is showing, as a file the worker
   * writes — always a job and never a synchronous download, whatever the row
   * count (docs/decisions/0043-the-worker.md §7).
   */
  exportDonationTotals: oc
    .route({ method: 'POST', path: '/reports/donation-totals/export' })
    .errors(guarded)
    .input(donationTotalsInputSchema)
    .output(generatedFileSchema),

  exportCampaignPerformance: oc
    .route({ method: 'POST', path: '/reports/campaign-performance/export' })
    .errors(guarded)
    .input(campaignPerformanceInputSchema)
    .output(generatedFileSchema),

  exportDonorSegments: oc
    .route({ method: 'POST', path: '/reports/donor-segments/export' })
    .errors(guarded)
    .input(reportWindowSchema)
    .output(generatedFileSchema),

  exportSeasonComparison: oc
    .route({ method: 'POST', path: '/reports/season-comparison/export' })
    .errors(guarded)
    .input(seasonComparisonInputSchema)
    .output(generatedFileSchema),
};
